import { ElementRef, Injectable } from '@angular/core';
import { PosHttpService } from '../../services/pos.http.service';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { FnAppSessionService } from 'foundation';

@Injectable({
  providedIn: 'root'
})
export class SiteClockService {
  elementRef: any;

  siteClockData: SchemaClockReport[] = [];

  constructor(private posHttpSvcs: PosHttpService, private apSessionSvcs: FnAppSessionService) { }

  setSiteClockData(data: SchemaClockReport[]): void {
    this.siteClockData = data;
  }

  createCharts(elementRef: ElementRef): void {
    this.elementRef = elementRef;
    const chartContainer = this.elementRef.nativeElement.querySelector('#site_clock');
    if (!chartContainer) {
      console.error('Chart container not found');
      return;
    }

    chartContainer.innerHTML = '';

    this.siteClockData.forEach((schemaData, index) => {
      const schemaKey = Object.keys(schemaData)[0];
      const data = schemaData[schemaKey];

      const chartDiv = document.createElement('div');
      chartDiv.id = `chart_${schemaKey}_${index}`;
      chartDiv.style.width = '100%';
      chartDiv.style.height = '400px';
      chartDiv.style.marginBottom = '20px';
      chartContainer.appendChild(chartDiv);

      const root = am5.Root.new(chartDiv);
      if (root._logo) {
        root._logo.dispose();
      }

      root.container.set("background", am5.Rectangle.new(root, {
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
        fillOpacity: 1
      }));

      root.setThemes([am5themes_Animated.new(root)]);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: true,
          wheelX: 'panX',
          wheelY: 'zoomX',
          layout: root.verticalLayout,
          paddingLeft: 10,
          paddingRight: 20,
        })
      );

      chart.children.unshift(
        am5.Label.new(root, {
          text: `${schemaKey}`,
          fontSize: 18,
          fill: am5.color(0xFF3333),
          textAlign: 'center',
          x: am5.percent(50),
          centerX: am5.percent(50),
          fontWeight: 'bold',
        })
      );

      const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 50 });
      xAxisRenderer.grid.template.setAll({ visible: false });
      xAxisRenderer.labels.template.setAll({
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -45,
        fontSize: 12,
        paddingTop: 10,
      });
      xAxisRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        strokeOpacity: 1,
        strokeWidth: 1,
      });
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          renderer: xAxisRenderer,
          categoryField: 'reporttime',
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
      xAxis.data.setAll(data);
      xAxis.children.push(
        am5.Label.new(root, {
          text: 'Report Time',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          x: am5.percent(50),
          centerX: am5.percent(50),
          y: am5.percent(100),
          centerY: am5.percent(100),
          paddingTop: 5,
        })
      );

      const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
      yAxisLeftRenderer.grid.template.setAll({
        visible: false,
      });
      yAxisLeftRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        strokeOpacity: 1,
        strokeWidth: 1,
      });
      yAxisLeftRenderer.labels.template.setAll({
        fontSize: 12,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        visible: true,
        paddingRight: 10,
      });
      const yAxisLeft = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yAxisLeftRenderer,
          tooltip: am5.Tooltip.new(root, {}),
          min: 0,
          extraMax: 0.1,
          numberFormat: '#',
        })
      );
      yAxisLeft.children.unshift(
        am5.Label.new(root, {
          text: 'Offline Count',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          rotation: -90,
          x: -2,
          y: am5.percent(50),
          centerY: am5.percent(50),
          centerX: am5.percent(50),
        })
      );

      const barSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Offline Count',
          xAxis: xAxis,
          yAxis: yAxisLeft,
          valueYField: 'count',
          categoryXField: 'reporttime',
          tooltip: am5.Tooltip.new(root, { labelText: 'Offline Count: {valueY}' }),
        })
      );
      barSeries.data.setAll(data);
      barSeries.columns.template.setAll({
        maxWidth: 30,
        width: am5.percent(80),
        fill: am5.color(0x87CEFA),
        strokeOpacity: 0,
      });

      barSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fontSize: 12,
            fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x1488F2 : 0x000000),
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            dy: -10,
            populateText: true,
          }),
        })
      );

      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
      cursor.lineX.set('visible', false);
      cursor.lineY.set('visible', false);

      const countMax = Math.max(...data.map(d => d.count));
      const countMin = Math.min(...data.map(d => d.count));
      chart.children.push(
        am5.Label.new(root, {
          text: `[#87CEFA]■[/] Offline Count: Max: ${countMax}, Min: ${countMin}`,
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          textAlign: 'left',
          x: am5.percent(0),
          centerX: am5.percent(0),
          paddingLeft: 10,
          paddingTop: 10,
          paddingBottom: 10,
        })
      );

      am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        dataSource: data,
      });
    });
  }
}

interface SchemaClockReport {
  [schema: string]: SiteClockReport[];
}

interface SiteClockReport {
  reporttime: string;
  count: number;
}